import { useEffect, useState } from 'react';
import { Select } from './ui';
import { api } from '../lib/api';
import { LINE_CODE_GROUPS, isPresetLineCode, isValidLineCode, lineCodeLabel, effectiveLineCode } from '../lib/fpaLineCodes';

// Inline account → FP&A-line picker, shared by the Accounts tab, the mapping
// coverage punch-list, and the Actuals "Unmapped" quick-map. Saves on change
// (preset) or on blur (custom code); shows the classification fallback as the
// placeholder so an unmapped account still reads as where it resolves today.

const CUSTOM = '__custom__';
const inputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

export function LineCodeMapSelect({ qboAccountId, lineCode, classification, disabled, onSaved }: {
  qboAccountId: string; lineCode: string | null; classification: string | null; disabled?: boolean; onSaved: () => void;
}) {
  const isCustom = (c: string | null) => c != null && !isPresetLineCode(c);
  const [custom, setCustom] = useState(isCustom(lineCode));
  const [customVal, setCustomVal] = useState(isCustom(lineCode) ? lineCode! : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setCustom(isCustom(lineCode));
    setCustomVal(isCustom(lineCode) ? lineCode! : '');
    setErr(null);
  }, [lineCode]);

  async function save(next: string | null) {
    setErr(null); setSaving(true);
    try { await api.fpa.setLineMap(qboAccountId, next); onSaved(); }
    catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  }

  if (disabled) {
    return lineCode
      ? <span style={{ fontSize: 11 }}>{lineCodeLabel(lineCode)}</span>
      : <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Unmapped</span>;
  }

  function onSelect(v: string) {
    if (v === CUSTOM) { setCustom(true); return; }
    setCustom(false);
    save(v === '' ? null : v);
  }
  function saveCustom() {
    const next = customVal.trim();
    if (next === '') { save(null); return; }
    if (!isValidLineCode(next)) { setErr('Use rev.*, cost.salary, cost.var.* or cost.fixed.*'); return; }
    save(next);
  }

  const fallback = effectiveLineCode(null, classification);
  const selectValue = custom ? CUSTOM : (lineCode ?? '');

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Select value={selectValue} onChange={onSelect}>
        <option value="">— Unmapped{fallback ? ` → ${lineCodeLabel(fallback)}` : ''} —</option>
        {LINE_CODE_GROUPS.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </optgroup>
        ))}
        <option value={CUSTOM}>Custom code…</option>
      </Select>
      {custom && (
        <input
          autoFocus value={customVal} placeholder="e.g. cost.fixed.insurance"
          onChange={(e) => setCustomVal(e.target.value)}
          onBlur={saveCustom}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          style={{ ...inputStyle, width: 180 }}
        />
      )}
      {saving && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>saving…</span>}
      {err && <span style={{ fontSize: 10, color: 'var(--color-red-text)' }}>{err}</span>}
    </span>
  );
}

/** Account → department override picker. "(by class)" = no override; the class
 *  dimension decides. Use for department-specific accounts (e.g. Salaries – Eng). */
export function AccountDepartmentSelect({ qboAccountId, departmentId, departments, disabled, onSaved }: {
  qboAccountId: string; departmentId: string | null; departments: Array<{ id: string; name: string }>; disabled?: boolean; onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  if (disabled) {
    const name = departments.find((d) => d.id === departmentId)?.name;
    return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{name ?? (departmentId ?? 'by class')}</span>;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Select value={departmentId ?? ''} onChange={async (v) => { setSaving(true); try { await api.fpa.setAccountDepartment(qboAccountId, v || null); onSaved(); } finally { setSaving(false); } }}>
        <option value="">(by class)</option>
        {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </Select>
      {saving && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>saving…</span>}
    </span>
  );
}
